import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

import { createHeadersFormOptions } from "../../dataProvider/fetch";
import { API_URL } from "../../dataProvider/API";
import {
  GET_QUALIFYING_SCORES,
  GET_QUALIFYING_SCORE_BY_ID,
  SAVE_QUALIFYING_SCORE,
  DELETE_QUALIFYING_SCORE,
  CLONE_QUALIFYING_SCORES,
  EXPORT_QUALIFYING_SCORES,
  GET_QUALIFYING_SCORE_TEMPLATES,
  GET_QUALIFYING_SCORE_FILTER_VALUES,
} from "../../constants/endpoints";
import { ALERT_TEXT } from "../../constants/titles";

const initialState = {
  loading: false,
  saving: false,
  list: {
    rows: [],
    currentPage: 0,
    totalPages: 0,
    totalElements: 0,
  },
  messageTemplates: [],
  filterValues: { divisions: [], regions: [] },
  error: null,
};

const buildUrl = (path) => `${API_URL}/${path}`;

const extractErrorMessage = (text, status) => {
  if (status === 403) return ALERT_TEXT.FORBIDDEN_QUALIFYING_SCORES;
  if (!text) return `Request failed (${status})`;
  try {
    const parsed = JSON.parse(text);
    return (
      parsed?.message ||
      parsed?.error ||
      parsed?.detail ||
      (typeof parsed === "string" ? parsed : `Request failed (${status})`)
    );
  } catch (_) {
    return text || `Request failed (${status})`;
  }
};

// fetchJson always resolves and parses body as JSON — it doesn't reject on 4xx/5xx
// and crashes on empty bodies. This wrapper checks status, throws the backend message
// on errors, and tolerates empty bodies.
const requestJson = async (url, options = {}) => {
  const headers = createHeadersFormOptions(options);
  const response = await fetch(url, { ...options, headers });
  const text = await response.text();
  if (!response.ok) {
    throw new Error(extractErrorMessage(text, response.status));
  }
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (_) {
    return text;
  }
};

export const fetchQualifyingScores = createAsyncThunk(
  "qualifyingScores/fetch",
  async ({ params }, { rejectWithValue }) => {
    try {
      return await requestJson(buildUrl(GET_QUALIFYING_SCORES(params)));
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to load qualifying scores");
    }
  },
);

export const fetchQualifyingScoreById = createAsyncThunk(
  "qualifyingScores/fetchById",
  async ({ id }, { rejectWithValue }) => {
    try {
      return await requestJson(buildUrl(GET_QUALIFYING_SCORE_BY_ID(id)));
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to load qualifying score");
    }
  },
);

export const saveQualifyingScore = createAsyncThunk(
  "qualifyingScores/save",
  async ({ data }, { rejectWithValue }) => {
    try {
      return await requestJson(buildUrl(SAVE_QUALIFYING_SCORE), {
        method: "POST",
        body: JSON.stringify(data),
      });
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to save qualifying score");
    }
  },
);

export const deleteQualifyingScore = createAsyncThunk(
  "qualifyingScores/delete",
  async ({ id }, { rejectWithValue }) => {
    try {
      await requestJson(buildUrl(DELETE_QUALIFYING_SCORE(id)), { method: "DELETE" });
      return id;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to delete qualifying score");
    }
  },
);

export const cloneQualifyingScores = createAsyncThunk(
  "qualifyingScores/clone",
  async ({ data }, { rejectWithValue }) => {
    try {
      return await requestJson(buildUrl(CLONE_QUALIFYING_SCORES), {
        method: "POST",
        body: JSON.stringify(data),
      });
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to copy qualifying scores");
    }
  },
);

// Plain requests for bulk work: no toasts and no `saving` flag, so a run over many rows reports
// its own progress and per-row errors instead of stacking a toast per failure.
export const getQualifyingScore = (id) =>
  requestJson(buildUrl(GET_QUALIFYING_SCORE_BY_ID(id)));

export const postQualifyingScore = (data) =>
  requestJson(buildUrl(SAVE_QUALIFYING_SCORE), {
    method: "POST",
    body: JSON.stringify(data),
  });

// Every row matching the list filters (all pages), for "select all matching".
export const fetchAllMatchingQualifyingScores = async (params, total) => {
  const json = await requestJson(
    buildUrl(GET_QUALIFYING_SCORES({ ...params, page: 0, size: Math.max(total || 0, 1) })),
  );
  return Array.isArray(json?.qualifyingScoresDto) ? json.qualifyingScoresDto : [];
};

/**
 * QUALIFYING_SCORES templates this governing body's scores may use:
 * `[{id, description, active, localeIds}]`. `includeId` keeps a score's current template in the
 * list even after it was deactivated.
 */
export const fetchQualifyingScoreMessageTemplates = createAsyncThunk(
  "qualifyingScores/fetchMessageTemplates",
  async ({ governingBodyId, includeId } = {}, { rejectWithValue }) => {
    try {
      const json = await requestJson(
        buildUrl(GET_QUALIFYING_SCORE_TEMPLATES(governingBodyId, includeId)),
      );
      return Array.isArray(json) ? json : [];
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to load message templates");
    }
  },
);

/** Division and Region values in use for the governing body, for the filter suggestions. */
export const fetchQualifyingScoreFilterValues = createAsyncThunk(
  "qualifyingScores/fetchFilterValues",
  async ({ governingBodyId }, { rejectWithValue }) => {
    try {
      const json = await requestJson(
        buildUrl(GET_QUALIFYING_SCORE_FILTER_VALUES(governingBodyId)),
      );
      return {
        divisions: Array.isArray(json?.divisions) ? json.divisions : [],
        regions: Array.isArray(json?.regions) ? json.regions : [],
      };
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to load filter values");
    }
  },
);

// Browser-side download via blob + anchor click — auth header is required so we can't just window.open.
export const downloadQualifyingScoresExport = async ({ format, params }) => {
  const url = buildUrl(EXPORT_QUALIFYING_SCORES(format, params));
  const headers = createHeadersFormOptions({ method: "GET" });
  // Accept hint helps the backend negotiate.
  headers.set(
    "Accept",
    format === "csv"
      ? "text/csv"
      : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  );
  const response = await fetch(url, { method: "GET", headers });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(extractErrorMessage(text, response.status));
  }
  const disposition = response.headers.get("Content-Disposition") || "";
  const match = /filename="?([^"]+)"?/i.exec(disposition);
  const fallbackName = `qualifying-scores.${format}`;
  const fileName = match ? match[1] : fallbackName;

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
};

const slice = createSlice({
  name: "qualifyingScores",
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchQualifyingScores.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQualifyingScores.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload || {};
        state.list = {
          rows: Array.isArray(payload.qualifyingScoresDto)
            ? payload.qualifyingScoresDto
            : [],
          currentPage: payload.currentPage ?? 0,
          totalPages: payload.totalPages ?? 0,
          totalElements: payload.totalElements ?? 0,
        };
      })
      .addCase(fetchQualifyingScores.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message || "Failed to load";
        toast.error(ALERT_TEXT.BAD_REQUEST);
      })

      .addCase(saveQualifyingScore.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveQualifyingScore.fulfilled, (state, action) => {
        state.saving = false;
        const isEdit = Boolean(action.meta?.arg?.data?.id);
        toast.success(
          isEdit
            ? ALERT_TEXT.SUCCESS_UPDATE_QUALIFYING_SCORE
            : ALERT_TEXT.SUCCESS_CREATE_QUALIFYING_SCORE,
        );
      })
      .addCase(saveQualifyingScore.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload || action.error?.message || "Failed to save";
        toast.error(state.error);
      })

      .addCase(deleteQualifyingScore.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(deleteQualifyingScore.fulfilled, (state) => {
        state.saving = false;
        toast.success(ALERT_TEXT.SUCCESS_DELETE_QUALIFYING_SCORE);
      })
      .addCase(deleteQualifyingScore.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload || action.error?.message || "Failed to delete";
        toast.error(state.error);
      })

      .addCase(cloneQualifyingScores.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(cloneQualifyingScores.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(cloneQualifyingScores.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload || action.error?.message || "Failed to copy";
        toast.error(state.error);
      })

      .addCase(fetchQualifyingScoreFilterValues.fulfilled, (state, action) => {
        state.filterValues = action.payload || { divisions: [], regions: [] };
      })
      .addCase(fetchQualifyingScoreFilterValues.rejected, (state) => {
        // Suggestions are optional; the page falls back to values from the loaded rows.
        state.filterValues = { divisions: [], regions: [] };
      })
      .addCase(fetchQualifyingScoreMessageTemplates.pending, (state) => {
        state.messageTemplates = [];
      })
      .addCase(fetchQualifyingScoreMessageTemplates.fulfilled, (state, action) => {
        state.messageTemplates = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchQualifyingScoreMessageTemplates.rejected, (state, action) => {
        state.error =
          action.payload || action.error?.message || "Failed to load templates";
      });
  },
});

export const { clearError } = slice.actions;
export default slice.reducer;
